import { connected, disconnected, messageReceived, errorOccurred } from '../slices/aviatorSocketSlice';
import { performGlobalLogout } from '../slices/authSlice';
import { updateAviatorRoundFromWebSocket } from '../slices/casinoResultsSlice';

let activeAviatorSocket = null;
let reconnectTimer = null;
let reconnectEndpoint = null;
let reconnectAttempts = 0;

const normalizeRoundId = (value) => {
	if (value == null || value === '') return null;
	const numericValue = Number(value);
	return Number.isFinite(numericValue) ? numericValue : null;
};

const clearReconnectTimer = () => {
	if (reconnectTimer) {
		clearTimeout(reconnectTimer);
		reconnectTimer = null;
	}
};

// Detach all handlers from a socket so it can no longer affect Redux state,
// then close it. Used whenever we're replacing or tearing down a connection.
const detachAndClose = (socket) => {
	if (!socket) return;
	socket.onopen = null;
	socket.onmessage = null;
	socket.onerror = null;
	socket.onclose = null;
	try {
		socket.close();
	} catch (err) {
		// socket may already be closing/closed - safe to ignore
	}
};

export const aviatorSocketMiddleware = (store) => (next) => (action) => {
	if (action.type === 'aviator/connect') {
		try {
			clearReconnectTimer();
			reconnectEndpoint = action.payload;
			// Tear down any existing connection first so its (possibly delayed)
			// events can never fire after the new socket takes over.
			if (activeAviatorSocket) {
				detachAndClose(activeAviatorSocket);
				activeAviatorSocket = null;
			}

			const endpoint = action.payload;
			const socket = new WebSocket(endpoint);
			activeAviatorSocket = socket;

			// Every handler below checks `activeAviatorSocket === socket` before
			// dispatching. This guards against a stale/late event from a socket
			// that has since been replaced (e.g. User A's socket closing after
			// User B has already connected) from ever touching the store.
			socket.onopen = () => {
				if (activeAviatorSocket === socket) {
						reconnectAttempts = 0;
					store.dispatch(connected());
				}
			};

			socket.onmessage = (event) => {
				if (activeAviatorSocket !== socket) return;
				try {
					const data = JSON.parse(event.data);
					if (!data) return;

					const currentRoundId = normalizeRoundId(store.getState().aviator?.roundId);
					const incomingRoundId = normalizeRoundId(data.roundId);
					if (currentRoundId != null && incomingRoundId != null && incomingRoundId < currentRoundId) {
						return;
					}

					store.dispatch(messageReceived(data));
					
						// Check if this is a completed round result
						if (data.phase === "CRASHED" && data.roundId) {
							// Dispatch to update casinoResults.aviator with the completed round
							// This is the CRITICAL FIX for Round History
							store.dispatch(updateAviatorRoundFromWebSocket({
								roundId: data.roundId,
								multiplier: data.multiplier,
								crashTime: data.timestamp || Date.now(),
							}));
						}
				} catch (error) {
						store.dispatch(errorOccurred('Invalid JSON in socket message'));
					}
				};

			socket.onerror = () => {
				if (activeAviatorSocket === socket) {
					store.dispatch(errorOccurred('WebSocket error'));
				}
			};

			socket.onclose = () => {
				if (activeAviatorSocket === socket) {
					store.dispatch(disconnected());
					activeAviatorSocket = null;
						if (reconnectEndpoint && navigator.onLine !== false) {
							const delay = Math.min(1500 * (2 ** reconnectAttempts), 15000);
							reconnectAttempts += 1;
							reconnectTimer = setTimeout(() => {
								reconnectTimer = null;
								if (!activeAviatorSocket && reconnectEndpoint && navigator.onLine !== false) {
									store.dispatch({ type: 'aviator/connect', payload: reconnectEndpoint });
								}
							}, delay);
						}
				}
			};
		} catch {
			store.dispatch(errorOccurred('WebSocket init failed'));
		}
	}

	if (action.type === 'aviator/disconnect') {
		clearReconnectTimer();
		reconnectEndpoint = null;
		if (activeAviatorSocket) {
			detachAndClose(activeAviatorSocket);
			activeAviatorSocket = null;
		}
		// Dispatch synchronously rather than waiting for the async onclose event,
		// so state updates immediately and can't race with a new connection.
		store.dispatch(disconnected());
	}

	if (action.type === 'aviator/send' && activeAviatorSocket?.readyState === WebSocket.OPEN) {
		const msg = typeof action.payload === 'string' ? action.payload : JSON.stringify(action.payload);
		activeAviatorSocket.send(msg);
	}

	// Handle global logout - disconnect WebSocket
	if (action.type === performGlobalLogout.fulfilled.type) {
		clearReconnectTimer();
		reconnectEndpoint = null;
		if (activeAviatorSocket) {
			detachAndClose(activeAviatorSocket);
			activeAviatorSocket = null;
		}
		// Dispatch synchronously so aviatorSocketSlice state is cleared
		// immediately on logout, instead of depending on the closing socket's
		// async onclose - which could otherwise fire after the next user's
		// socket has connected.
		store.dispatch(disconnected());
	}

	return next(action);
};

export default aviatorSocketMiddleware;